import * as React from "react";

import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "ui/components/popover";

import { Badge } from "ui/components/badge";
import { Button } from "ui/components/button";
import { Label } from "ui/components/label";
import { cn } from "ui/lib/utils";

export default function MultiComboboxBadges({
  data = [],
  values = [],
  onChange,
  // Optional resolver for selected values not present in current data
  fetchSelected, // async (value) => mappedItem { value,label,select }
  // Mapping controls (default mapping lives here)
  mapItem,
  valueKey = "id",
  labelKey = "name",
  selectKey,
  searchable = true,
  search = "",
  onSearch,
  shouldFilter = false,
  label,
  required = false,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No items found.",
  isLoading = false,
  disabled = false,
  className,
  clearSearchWhenItemSelect = false,
  hideSelectedInList = true,
  badgeClassName = "",
  clearable = true,
  clearAriaLabel = "Clear selection",
  maxBadges = 10,
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedByValue, setSelectedByValue] = React.useState({}); // value(string) -> item
  const selectedByValueRef = React.useRef(selectedByValue);
  React.useEffect(() => {
    selectedByValueRef.current = selectedByValue;
  }, [selectedByValue]);

  const normalizeOne = React.useCallback(
    (item) => {
      if (!item) return null;
      if (mapItem) return mapItem(item);
      if (
        item.value !== undefined &&
        (item.label !== undefined || item.select !== undefined)
      )
        return item;
      const derivedValue = item?.[valueKey];
      const derivedLabel = item?.[labelKey];
      const derivedSelect =
        selectKey && item?.[selectKey] ? item[selectKey] : derivedLabel;
      return {
        value: derivedValue,
        label: derivedLabel,
        select: derivedSelect,
      };
    },
    [mapItem, valueKey, labelKey, selectKey]
  );

  const normalizedData = React.useMemo(
    () => data.map(normalizeOne).filter(Boolean),
    [data, normalizeOne]
  );

  // Fast lookup for current page data by value
  const dataByValue = React.useMemo(() => {
    const map = {};
    for (const item of normalizedData) map[String(item?.value)] = item;
    return map;
  }, [normalizedData]);

  // Keep a stable view of selected items regardless of current filtered data
  React.useEffect(() => {
    let cancelled = false;

    const shallowEqual = (a, b) => {
      const ak = Object.keys(a);
      const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      for (const k of ak) if (a[k] !== b[k]) return false;
      return true;
    };

    const sync = async () => {
      const current = selectedByValueRef.current;
      const baseNext = {};
      const pending = [];

      for (const raw of values) {
        const key = typeof raw === "object" && raw !== null ? String(raw.value ?? raw.id ?? "") : String(raw);
        const candidate = dataByValue[key] || current[key];
        if (candidate) baseNext[key] = candidate;
        else pending.push({ key, raw });
      }

      if (pending.length === 0) {
        if (!shallowEqual(current, baseNext) && !cancelled)
          setSelectedByValue(baseNext);
        return;
      }

      if (fetchSelected) {
        try {
          const fetched = await Promise.all(
            pending.map(async ({ key, raw }) => {
              try {
                const item = await fetchSelected(raw);
                return {
                  key,
                  item: item
                    ? normalizeOne(item)
                    : normalizeOne({ [valueKey]: raw }),
                };
              } catch {
                return { key, item: normalizeOne({ [valueKey]: raw }) };
              }
            })
          );
          for (const { key, item } of fetched) baseNext[key] = item;
        } catch {}
      } else {
        for (const { key, raw } of pending)
          baseNext[key] = normalizeOne({ [valueKey]: raw });
      }

      if (!cancelled) setSelectedByValue(baseNext);
    };

    sync();

    return () => {
      cancelled = true;
    };
  }, [values, dataByValue, fetchSelected, normalizeOne, valueKey]);

  const selectedItems = React.useMemo(
    () => values.map((v) => selectedByValue[String(v)]).filter(Boolean),
    [values, selectedByValue]
  );

  const filteredData = React.useMemo(() => {
    const base = hideSelectedInList
      ? normalizedData.filter(
          (d) => !values.some((v) => String(v) === String(d.value))
        )
      : normalizedData;
    if (!shouldFilter || !search) return base;
    const s = String(search).toLowerCase();
    return base.filter((d) =>
      String(d.label ?? d.select ?? d.value)
        .toLowerCase()
        .includes(s)
    );
  }, [normalizedData, values, hideSelectedInList, shouldFilter, search]);

  const toggleValue = (val) => {
    const exists = values.some((v) => String(v) === String(val));
    const next = exists
      ? values.filter((v) => String(v) !== String(val))
      : [...values, val];
    onChange?.(next);
    if (clearSearchWhenItemSelect) onSearch?.("");

    // Clean cache when removing
    if (exists) {
      const key = String(val);
      setSelectedByValue((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleClearAll = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onSearch?.("");
    onChange?.([]);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label
          className={cn(
            "text-sm",
            required && "after:content-['*'] after:ml-0.5 after:text-red-500"
          )}
        >
          {label}
        </Label>
      )}

      <Popover
        open={open}
        onOpenChange={setOpen}
        disabled={disabled || isLoading}
        modal
      >
        <PopoverTrigger asChild disabled={disabled || isLoading}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-busy={isLoading}
            disabled={disabled || isLoading}
            className={cn("w-full justify-between h-full")}
          >
            <div className="flex items-center gap-1 flex-1 min-w-0 ">
              <div className="flex items-center gap-1 flex-wrap  min-w-0">
                {selectedItems.length === 0 && (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
                {selectedItems.slice(0, maxBadges).map((item) => (
                  <Badge
                    key={item.value}
                    variant="outline"
                    className={cn(
                      "flex items-center gap-1 shrink-0",
                      badgeClassName
                    )}
                  >
                    <span className="">{item.select ?? item.label}</span>
                    <span
                      role="button"
                      aria-label="Remove"
                      className="cursor-pointer hover:bg-zinc-200 rounded-full"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleValue(item.value);
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </span>
                  </Badge>
                ))}
                {selectedItems.length > maxBadges && (
                  <Badge variant="outline">
                    +{selectedItems.length - maxBadges} more
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {clearable && values.length > 0 && !isLoading && (
                <span
                  role="button"
                  aria-label={clearAriaLabel}
                  className="opacity-70 hover:opacity-100 cursor-pointer"
                  onClick={handleClearAll}
                >
                  <X className="h-4 w-4" />
                </span>
              )}
              {isLoading ? (
                <Loader2 className="opacity-70 animate-spin" />
              ) : (
                <ChevronsUpDown className="opacity-50" />
              )}
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className={cn("p-0 !w-[var(--radix-popover-trigger-width)]")}
        >
          <Command shouldFilter={false}>
            {searchable && (
              <div className="relative px-0.5 py-0.5">
                <CommandInput
                  placeholder={searchPlaceholder}
                  className="h-9 pr-8"
                  value={search}
                  onValueChange={onSearch}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                    onClick={() => onSearch?.("")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            <CommandList>
              {filteredData.length === 0 && !isLoading ? (
                <CommandEmpty>{emptyMessage}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredData.map((item) => {
                    const checked = values.some(
                      (v) => String(v) === String(item.value)
                    );
                    return (
                      <CommandItem
                        key={item.value}
                        value={String(item.value)}
                        onSelect={() => toggleValue(item.value)}
                      >
                        {item.label}
                        <Check
                          className={cn(
                            "ml-auto",
                            checked ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
